import React from 'react';
import { Link } from 'react-router-dom';
import { User, Calendar, Target, AlertCircle, Building2 } from 'lucide-react';
import KanbanBoard from './KanbanBoard';
import './ActionItemsKanban.css';

/**
 * Kanban board for Action Items.
 *
 * Action items only carry two statuses (Open / Completed), so a status-only
 * board would be two columns wide. The board therefore supports three groupings
 * and defaults to due date, which is what the list is actually worked from:
 *
 *   due_date    - Overdue / Due Today / This Week / Later / No Due Date / Completed
 *                 Dragging reschedules the item (or completes it).
 *   status      - Open / Completed. Dragging completes or reopens.
 *   assigned_to - One column per active user plus Unassigned. Dragging reassigns.
 *
 * Every drop is expressed as a partial update and handed to `onItemChange`,
 * which sends it to PATCH /api/tasks/:id.
 */

export const GROUP_BY_OPTIONS = [
  { value: 'due_date', label: 'Due Date' },
  { value: 'status', label: 'Status' },
  { value: 'assigned_to', label: 'Assignee' },
];

// --- Date helpers -----------------------------------------------------------

const startOfDay = (value) => {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
};

/* "This week" runs through the upcoming Sunday, matching the Due Date filter
   dropdown on the list view so the two never disagree about a given item. */
const endOfWeekFrom = (today) => {
  const d = new Date(today);
  d.setDate(today.getDate() + (7 - today.getDay()));
  d.setHours(23, 59, 59, 999);
  return d;
};

/* datetime-local format — the same shape the create/edit forms submit. */
const toLocalDateTimeString = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const atEndOfBusinessDay = (date) => {
  const d = new Date(date);
  d.setHours(17, 0, 0, 0);
  return toLocalDateTimeString(d);
};

// --- Due-date bucketing -----------------------------------------------------

const DUE_BUCKETS = [
  { id: 'overdue', title: 'Overdue', color: 'danger', droppable: false, emptyMessage: 'Nothing overdue' },
  { id: 'today', title: 'Due Today', color: 'warning' },
  { id: 'week', title: 'This Week', color: 'primary' },
  { id: 'later', title: 'Later', color: 'info' },
  { id: 'none', title: 'No Due Date', color: 'gray' },
  { id: 'completed', title: 'Completed', color: 'success', emptyMessage: 'Drop here to complete' },
];

function dueBucketFor(item, today, endOfWeek) {
  if (item.status === 'Completed') return 'completed';
  if (!item.due_date) return 'none';
  const due = startOfDay(item.due_date);
  if (due < today) return 'overdue';
  if (due.getTime() === today.getTime()) return 'today';
  if (new Date(item.due_date) <= endOfWeek) return 'week';
  return 'later';
}

function dueBucketPatch(bucketId, today, endOfWeek) {
  switch (bucketId) {
    case 'today':
      return { due_date: atEndOfBusinessDay(today), status: 'Open' };
    case 'week':
      return { due_date: atEndOfBusinessDay(endOfWeek), status: 'Open' };
    case 'later': {
      const later = new Date(endOfWeek);
      later.setDate(later.getDate() + 7);
      return { due_date: atEndOfBusinessDay(later), status: 'Open' };
    }
    case 'none':
      return { due_date: null, status: 'Open' };
    case 'completed':
      return { status: 'Completed' };
    default:
      return null;
  }
}

// --- Column builders --------------------------------------------------------

function buildDueDateColumns(items, today, endOfWeek) {
  return DUE_BUCKETS.map((bucket) => ({
    ...bucket,
    items: items.filter((item) => dueBucketFor(item, today, endOfWeek) === bucket.id),
  }));
}

function buildStatusColumns(items) {
  return [
    { id: 'Open', title: 'Open', color: 'primary', items: items.filter((i) => i.status !== 'Completed') },
    {
      id: 'Completed',
      title: 'Completed',
      color: 'success',
      emptyMessage: 'Drop here to complete',
      items: items.filter((i) => i.status === 'Completed'),
    },
  ];
}

function buildAssigneeColumns(items, users) {
  const byId = new Map();
  users.forEach((u) => {
    byId.set(String(u.id), { id: String(u.id), title: u.name, color: 'primary', items: [] });
  });

  // An item can be assigned to someone who is no longer in the active-user list
  // (a deactivated user). Give them a column so the item stays on the board.
  items.forEach((item) => {
    if (!item.assigned_to) return;
    const key = String(item.assigned_to);
    if (!byId.has(key)) {
      byId.set(key, {
        id: key,
        title: item.assigned_to_name || `User #${key}`,
        color: 'gray',
        items: [],
      });
    }
  });

  items.forEach((item) => {
    if (item.assigned_to) byId.get(String(item.assigned_to)).items.push(item);
  });

  return [
    ...byId.values(),
    {
      id: 'unassigned',
      title: 'Unassigned',
      color: 'gray',
      items: items.filter((i) => !i.assigned_to),
    },
  ];
}

// --- Component --------------------------------------------------------------

function ActionItemsKanban({
  actionItems = [],
  users = [],
  groupBy = 'due_date',
  onItemChange,
  canEdit = true,
}) {
  const today = startOfDay(new Date());
  const endOfWeek = endOfWeekFrom(today);

  let columns;
  if (groupBy === 'status') {
    columns = buildStatusColumns(actionItems);
  } else if (groupBy === 'assigned_to') {
    columns = buildAssigneeColumns(actionItems, users);
  } else {
    columns = buildDueDateColumns(actionItems, today, endOfWeek);
  }

  const getItemColumnId = (item) => {
    if (groupBy === 'status') return item.status === 'Completed' ? 'Completed' : 'Open';
    if (groupBy === 'assigned_to') return item.assigned_to ? String(item.assigned_to) : 'unassigned';
    return dueBucketFor(item, today, endOfWeek);
  };

  const handleCardMove = (itemId, fromColumnId, toColumnId) => {
    let patch;
    if (groupBy === 'status') {
      patch = { status: toColumnId };
    } else if (groupBy === 'assigned_to') {
      patch = { assigned_to: toColumnId === 'unassigned' ? null : Number(toColumnId) };
    } else {
      patch = dueBucketPatch(toColumnId, today, endOfWeek);
    }
    if (patch) onItemChange(itemId, patch);
  };

  const isOverdue = (item) =>
    item.status === 'Open' && item.due_date && new Date(item.due_date) < new Date();

  const renderCard = (item) => (
    <>
      <div className={`kanban-card-title action-item-title${item.status === 'Completed' ? ' completed' : ''}`}>
        {item.title}
      </div>

      {item.account_name && (
        <Link to={`/accounts/${item.account_id}`} className="kanban-card-account">
          <Building2 size={12} /> {item.account_name}
        </Link>
      )}

      {item.opportunity_name && (
        <Link to={`/opportunities/${item.opportunity_id}`} className="kanban-card-account">
          <Target size={12} /> {item.opportunity_name}
        </Link>
      )}

      <div className="kanban-card-details">
        {item.due_date ? (
          <div className={`kanban-card-date${isOverdue(item) ? ' text-danger' : ''}`}>
            {isOverdue(item) ? <AlertCircle size={12} /> : <Calendar size={12} />}
            {new Date(item.due_date).toLocaleDateString()}
          </div>
        ) : (
          <div className="kanban-card-date">
            <Calendar size={12} /> No due date
          </div>
        )}
      </div>

      {groupBy !== 'assigned_to' && (
        <div className="kanban-card-owner">
          <User size={12} />
          {item.assigned_to_name || 'Unassigned'}
        </div>
      )}
    </>
  );

  return (
    <KanbanBoard
      columns={columns}
      onCardMove={handleCardMove}
      renderCard={renderCard}
      getItemId={(item) => item.id}
      getItemColumnId={getItemColumnId}
      canDrag={canEdit}
      emptyMessage={canEdit ? 'Drop action items here' : 'No action items'}
      className="action-items-kanban"
    />
  );
}

export default ActionItemsKanban;
